import { useState } from 'react';
import { TToolArgumentEditor } from "@intone/shared";
import { isSecretHeader } from '@intone/shared/utils';
import { Plus, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button.tsx';

type Props = {
  arg: TToolArgumentEditor;
  onChange: (patch: Partial<TToolArgumentEditor>) => void
  isError: boolean
}

export function ListKeyValueInput({arg, onChange, isError}: Props) {
  const [localPairs, setLocalPairs] = useState(() => {
    try {
      const obj = JSON.parse(String(arg.value) || '{}');
      const entries = Object.entries(obj).map(([key, value]) => ({ key, value: String(value) }));
      return entries.length > 0 ? entries : [{ key: '', value: '' }];
    } catch (e) {
      return [{ key: '', value: '' }];
    }
  });

  const updatePairs = (newPairs: { key: string; value: string }[]) => {
    setLocalPairs(newPairs);

    const obj: Record<string, string> = {};
    newPairs.forEach((p) => {
      if (p.key.trim()) { // Сохраняем только непустые ключи
        obj[p.key] = p.value;
      }
    });
    onChange({ value: JSON.stringify(obj) });
  };

  return (
    <div className="grid gap-2">
      {localPairs.map((pair, index) => (
        <div key={index} className="flex items-center gap-2">
          <div className="flex-1 grid grid-cols-2 gap-2">
            <Input
              value={pair.key}
              onChange={(e) => {
                const newPairs = [...localPairs];
                newPairs[index].key = e.target.value;
                updatePairs(newPairs);
              }}
              placeholder="Key"
              className={`h-7 ${isError && !pair.key.trim() ? 'border-destructive' : ''}`}
            />
            <Input
              value={pair.value}
              onChange={(e) => {
                const newPairs = [...localPairs];
                newPairs[index].value = e.target.value;
                updatePairs(newPairs);
              }}
              placeholder="Value"
              type={isSecretHeader(pair.key) ? 'password' : 'text'}
              autoComplete={isSecretHeader(pair.key) ? 'off' : undefined}
              className={`h-7 ${isError && !pair.value.trim() ? 'border-destructive' : ''}`}
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-6 w-6 p-0 text-destructive hover:text-white hover:bg-destructive border-destructive hover:border-destructive"
            onClick={() => {
              const newPairs = localPairs.filter((_, i) => i !== index);
              updatePairs(newPairs.length ? newPairs : [{ key: '', value: '' }]);
            }}
          >
            <Trash2 className="w-2.5 h-2.5" />
          </Button>
        </div>
      ))}
      <Button
        variant="outline"
        className="w-full h-8"
        onClick={() => updatePairs([...localPairs, { key: '', value: '' }])}
      >
        <Plus className="w-3 h-3 mr-2" />
        <span>Add Pair</span>
      </Button>
    </div>
  );
}